import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { ConversionRatesChartDataType } from "./ConversionRates";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import { TFunction } from "i18next";
import { cn } from "@/utils/utils";
import { dropdownPropsType } from "@/components/controls/Dropdown/Dropdown";
import { camelCase, Dictionary, fromPairs, map } from "lodash";

export const renderMilestone = (
  t: TFunction<"translation", undefined>,
  stageGroups: Dictionary<ConversionRatesChartDataType[]>,
  milestone: ConversionRatesChartDataType,
  index: number,
  additionalClassName: string = ""
) => {
  const numStages = stageGroups[milestone.milestone]?.length ?? 0;
  if (numStages === 0) return null;
  return (
    <div
      className={cn("flex gap-2 grow items-center", additionalClassName)}
      key={index}
    >
      <Avatar className="!size-5 !rounded-lg !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(milestone.milestone)
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Tooltip>
        <TooltipTrigger className="flex items-center">
          <Text as="div" variant="bodySecondary">
            {milestone.label}
          </Text>
        </TooltipTrigger>
        <TooltipContent
          data-test="stage-tooltip"
          className="flex flex-col gap-2 max-w-48"
        >
          <Text as="div" variant="labelSecondary">
            {milestone.label}
          </Text>
          <Text as="div" variant="body">
            {t(
              `stageAverageDuration.descriptions.${camelCase(milestone.label)}`
            )}
          </Text>
        </TooltipContent>
      </Tooltip>
      {numStages > 0 ? (
        <Tooltip>
          <TooltipTrigger className="text-tertiary text-sm flex items-center">
            {`${numStages} ${t("stageAverageDuration.stages")}`}
          </TooltipTrigger>
          <TooltipContent data-test="stages-tooltip">
            <Text as="div" variant="body" className="flex flex-col gap-1">
              {map(stageGroups[milestone.milestone], (stage, index) => (
                <div key={index}>{stage.label}</div>
              ))}
            </Text>
          </TooltipContent>
        </Tooltip>
      ) : (
        <span className="text-tertiary text-sm flex items-center">
          {`0 ${t("stageAverageDuration.stages")}`}
        </span>
      )}
    </div>
  );
};

export const periodsDropdownProps = (
  t: TFunction<"translation", undefined>
): dropdownPropsType => ({
  label: t("conversionRates.dealsClosedAt"),
  options: fromPairs(
    map(["allTimes", "ytd", "last12m", "last24m"], (timeframe) => [
      timeframe,
      t(`timeframes.${timeframe}`)
    ])
  ),
  defaultValue: "allTimes"
});

export const percentageComparisonChartLabels = (
  t: TFunction<"translation", undefined>
): string[] =>
  ["you", "team", "organization", "industry"].map((key) =>
    t(`comparison.titles.${key}`)
  );
